// CSV point-sample ingestion: parse a lab/GPS CSV, auto-match its columns to the
// point-sample attributes, then build the GeoJSON point set the save path uses.
import { CsvParseError, parseCsv, type ParsedCsv } from "./csvTokenize";

export { CsvParseError, parseCsv };
export type { ParsedCsv };

// ---- column targets + auto-matching ----------------------------------------------------

export interface CsvTarget {
  key: string;
  label: string;
  required?: boolean;
  aliases: string[];
  /*
   * ordered PRIORITY regex tiers in NORMALIZED-header space (lowercased, punctuation stripped)
   * when present these drive the match and `aliases` is just the legacy fallback shape
   */
  patterns?: RegExp[];
  /* * a ppm-basis analyte — eligible for lb/ac → ppm conversion when a "<col> UOM" neighbor says lb */
  ppm?: boolean;
}

// Headers that are NEVER analyte VALUES, whatever they contain: ratings ("VH"), fertilizer/lime
// recs unit-of-measure companions, crop/yield-goal blocks, the Haney H3A/water-extract panel (that
// upload has its own targets), SAR, and subsoil repeats. Without this gate "CEC_RATE" contains
// "cec" and a rec column can swallow a nutrient — built from the example-CSV census, 8 lab formats.
export const NON_VALUE_HEADER =
  /(rating|rate|rec\d*|uom|goal\d*)$|^(pastcrop|previouscrop|newcrop|pcrop|crop|yield|lime|gypsum|elementalsulfur|units|h3a|watersol|sar|excesslime|sub(no3|soil|depth))/;
export const IDENT_KEYS = new Set([
  "lat",
  "lon",
  "point_id",
  "sampled_at",
  "depth_in",
]);
/* * match-only: K_lbA → klba. Does NOT convert — event `units` is the user's declaration. */
const lbAcHeader = (el: string) => new RegExp(`^${el}lbs?(a|ac|acre)$`);

/*
 *
 * Anything else — or an impossible date — returns RAW so the server can refuse it BY NAME rather
 * than this function guessing; never null, because "no date" and "a date we couldn't read" are
 * different facts.
 */
export function isoDateOf(raw: string): string {
  const s = raw.trim();
  const iso = /^(\d{4})-(\d{1,2})-(\d{1,2})([T ].*)?$/.exec(s);
  const us = /^(\d{1,2})\s*[/.-]\s*(\d{1,2})\s*[/.-]\s*(\d{2}|\d{4})$/.exec(s);
  let y: number, m: number, d: number;
  if (iso) [y, m, d] = [Number(iso[1]), Number(iso[2]), Number(iso[3])];
  else if (us) {
    [m, d] = [Number(us[1]), Number(us[2])];
    if (m > 12 && d <= 12) [m, d] = [d, m];
    y = Number(us[3].length === 2 ? `20${us[3]}` : us[3]);
  } else return raw;
  const real =
    m >= 1 &&
    m <= 12 &&
    d >= 1 &&
    d <= 31 &&
    y >= 1990 &&
    y <= 2100 &&
    new Date(Date.UTC(y, m - 1, d)).getUTCDate() === d; // 2/30 etc. roll over — refuse, don't shift
  return real
    ? `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`
    : raw;
}

// `K_lbA` is a K results name — match it, do not convert from the suffix.
export const CSV_TARGETS: CsvTarget[] = [
  {
    key: "lat",
    label: "Latitude",
    required: true,
    aliases: [],
    patterns: [/^(lat|latitude|latdd|gpslat)$/, /^(y|pointy)$/],
  },
  {
    key: "lon",
    label: "Longitude",
    required: true,
    aliases: [],
    patterns: [
      /^(lon|lng|long|longitude|londd|gpslon|gpslong)$/,
      /^(x|pointx)$/,
    ],
  },
  {
    key: "point_id",
    label: "Point ID",
    aliases: [],
    patterns: [
      /^pointid$/,
      /^sampleid$/,
      /^sampleid1$/,
      /^(splid|osplid)$/,
      /^(id|sample|point|label|name|sampleno|samplenumber)$/,
    ],
  },
  // priority: the SAMPLING date over lab-processing dates (an export can carry four date columns)
  {
    key: "sampled_at",
    label: "Sample date",
    aliases: [],
    patterns: [
      /^datesampled$/,
      /^sampledate$/,
      /^date$/,
      /^(sampledat|sampled)$/,
      /^datereceived$/,
      /^(reportdate|datereported)$/,
      /^dateexported$/,
    ],
  },
  {
    key: "depth_in",
    label: "Depth (in)",
    aliases: [],
    patterns: [
      /^(depthend|enddepth)$/,
      /^(depth|depthin|depthinches|sampledepth)$/,
    ],
  },

  // BUFFER pH before pH so "BpH"-family headers can never fall through to the water pH
  {
    key: "buffer_ph",
    label: "Buffer pH",
    aliases: [],
    patterns: [/^bph/, /^bufferph$/, /^wdrfbuffer$/, /buffer/],
  },
  {
    key: "ph",
    label: "pH",
    aliases: [],
    patterns: [/^ph$/, /^ph11$/, /^soilph/, /^phwater$/],
  },
  {
    key: "om",
    label: "Organic matter (%)",
    aliases: [],
    patterns: [
      /^om$/,
      /^(ompercent|ompct)$/,
      /^organicmatter/,
      /^omloi$/,
      /^omwb$/,
      /^lo$/,
    ],
  },
  {
    key: "organic_c_pct",
    label: "Organic carbon (%)",
    aliases: [],
    patterns: [/^organiccarbon/, /^orgc$/],
  },
  {
    key: "cec",
    label: "CEC",
    aliases: [],
    patterns: [/^cec$/, /^cecmeq/, /^sumofcations/],
  },

  // base saturations BEFORE the elements, so "%"-basis columns can never land in a ppm slot
  {
    key: "k_sat_pct",
    label: "K saturation (%)",
    aliases: [],
    patterns: [
      /^(ksatpercent|ksaturation|percentk|perk|kpercent|potassiumsat)$/,
    ],
  },
  {
    key: "mg_sat_pct",
    label: "Mg saturation (%)",
    aliases: [],
    patterns: [
      /^(mgsatpercent|mgsaturation|percentmg|permg|mgpercent|magnesiumsat)$/,
    ],
  },
  {
    key: "ca_sat_pct",
    label: "Ca saturation (%)",
    aliases: [],
    patterns: [
      /^(casatpercent|casaturation|percentca|perca|capercent|calciumsat)$/,
    ],
  },
  {
    key: "h_sat_pct",
    label: "H saturation (%)",
    aliases: [],
    patterns: [
      /^(hsatpercent|hsaturation|percenth|perh|hpercent|hydrogensat)$/,
    ],
  },
  {
    key: "na_sat_pct",
    label: "Na saturation (%)",
    aliases: [],
    patterns: [
      /^(nasatpercent|nasaturation|percentna|perna|napercent|sodiumsat)$/,
    ],
  },
  {
    key: "base_sat_pct",
    label: "Base saturation (%)",
    aliases: [],
    patterns: [/^basesat(uration)?$/],
  },
  { key: "h_meq", label: "H (meq/100g)", aliases: [], patterns: [/^hmeq$/] },

  {
    key: "p",
    label: "Phosphorus (P)",
    ppm: true,
    aliases: [],
    patterns: [
      /^p$/,
      /^p1$/,
      /^pppm$/,
      lbAcHeader("p"),
      lbAcHeader("p1"),
      /^pbray1/,
      /^(phosphorusm3|m3phosphate)/,
      /^mehlich3?p$/,
      /^m3p$/,
      /^brayp$/,
      /^phosphorus(?!p2o5)/,
      /^phos$/,
    ],
  },
  {
    key: "p2",
    label: "Phosphorus Bray-2 (P2)",
    ppm: true,
    aliases: [],
    patterns: [/^p2$/, lbAcHeader("p2"), /^pbray2/],
  },
  {
    key: "p_olsen",
    label: "P Olsen / bicarb",
    ppm: true,
    aliases: [],
    patterns: [/^polsen/, /^pbicarb/, /^olsenp$/, /^bicarb$/],
  },
  {
    key: "k",
    label: "Potassium (K)",
    ppm: true,
    aliases: [],
    patterns: [
      /^k$/,
      /^kppm$/,
      /^m3k$/,
      lbAcHeader("k"),
      /^potassium(?!k2o|sat)/,
      /^nh4oacpotassium/,
    ],
  },
  {
    key: "s",
    label: "Sulfur (S)",
    ppm: true,
    aliases: [],
    patterns: [
      /^s$/,
      /^sppm$/,
      lbAcHeader("s"),
      /^so4s$/,
      /^(sulfurs|sulfur|sulphur)$/,
      /^(m3sulfur|sulfatem3)/,
    ],
  },
  {
    key: "ca",
    label: "Calcium (Ca)",
    ppm: true,
    aliases: [],
    patterns: [
      /^ca$/,
      /^cappm$/,
      lbAcHeader("ca"),
      /^calcium(?!sat)/,
      /^nh4oaccalcium/,
    ],
  },
  {
    key: "mg",
    label: "Magnesium (Mg)",
    ppm: true,
    aliases: [],
    patterns: [
      /^mg$/,
      /^mgppm$/,
      lbAcHeader("mg"),
      /^magnesium(?!sat)/,
      /^nh4oacmagnesium/,
    ],
  },
  {
    key: "na",
    label: "Sodium (Na)",
    ppm: true,
    aliases: [],
    patterns: [
      /^na$/,
      /^nappm$/,
      lbAcHeader("na"),
      /^sodium(?!sat)/,
      /^nh4oacsodium/,
    ],
  },
  {
    key: "zn",
    label: "Zinc (Zn)",
    ppm: true,
    aliases: [],
    patterns: [/^zn$/, /^znppm$/, lbAcHeader("zn"), /^zinc/, /^dtpazinc/],
  },
  {
    key: "mn",
    label: "Manganese (Mn)",
    ppm: true,
    aliases: [],
    patterns: [
      /^mn$/,
      /^mnppm$/,
      lbAcHeader("mn"),
      /^manganese/,
      /^dtpamanganese/,
    ],
  },
  {
    key: "fe",
    label: "Iron (Fe)",
    ppm: true,
    aliases: [],
    patterns: [/^fe$/, /^feppm$/, lbAcHeader("fe"), /^iron/, /^dtpairon/],
  },
  {
    key: "cu",
    label: "Copper (Cu)",
    ppm: true,
    aliases: [],
    patterns: [/^cu$/, /^cuppm$/, lbAcHeader("cu"), /^copper/, /^dtpacopper/],
  },
  {
    key: "b",
    label: "Boron (B)",
    ppm: true,
    aliases: [],
    patterns: [/^b$/, /^bppm$/, lbAcHeader("b"), /^boron/, /^h2oboron/],
  },
  {
    key: "al",
    label: "Aluminum (Al)",
    ppm: true,
    aliases: [],
    patterns: [/^al$/, lbAcHeader("al"), /^alm3/],
  },
  {
    key: "cl",
    label: "Chloride (Cl)",
    ppm: true,
    aliases: [],
    patterns: [/^cl$/, /^clppm$/, lbAcHeader("cl"), /^chloride/],
  },
  {
    key: "mo",
    label: "Molybdenum (Mo)",
    ppm: true,
    aliases: [],
    patterns: [/^mo$/, lbAcHeader("mo"), /^moly$/],
  },

  {
    key: "no3_n",
    label: "Nitrate-N (ppm)",
    ppm: true,
    aliases: [],
    patterns: [/^(no3|no3n|no3nppm)$/, /nitrate.*ppm/, /ppmno3n$/],
  },
  {
    key: "nh4_n",
    label: "Ammonium-N (ppm)",
    ppm: true,
    aliases: [],
    patterns: [/^(nh4|nh4n|nh4nppm)$/],
  },
  {
    key: "tkn",
    label: "Total Kjeldahl N",
    ppm: true,
    aliases: [],
    patterns: [/^tkn/],
  },
  { key: "enr", label: "ENR", aliases: [], patterns: [/^enr$/] },

  {
    key: "ec",
    label: "Soluble salts / EC (mmho/cm)",
    aliases: [],
    patterns: [
      /^solsalt/,
      /^solublesalt/,
      /electricalconductivity/,
      /^ec$/,
      /^ss$/,
    ],
  },
  { key: "sand_pct", label: "Sand (%)", aliases: [], patterns: [/^sand$/] },
  { key: "silt_pct", label: "Silt (%)", aliases: [], patterns: [/^silt$/] },
  { key: "clay_pct", label: "Clay (%)", aliases: [], patterns: [/^clay$/] },
  {
    key: "texture",
    label: "Soil texture",
    aliases: [],
    patterns: [/^(soil)?texture$/],
  },
  {
    key: "bulk_density",
    label: "Bulk density (g/cm3)",
    aliases: [],
    patterns: [/^bulkdensity/],
  },
  {
    key: "soil_moisture_pct",
    label: "Soil moisture (%)",
    aliases: [],
    patterns: [/^soilmoisture/],
  },
];

const norm = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, "");

function numericShare(
  rows: string[][],
  col: number,
  test: (n: number) => boolean,
): number {
  const sample = rows.slice(0, 25);
  let ok = 0;
  let seen = 0;
  for (const r of sample) {
    const v = (r[col] ?? "").trim();
    if (!v) continue;
    seen++;
    const n = Number(v);
    if (Number.isFinite(n) && test(n)) ok++;
  }
  return seen ? ok / seen : 0;
}

// Auto-match headers -> targets. Exact alias first, then contains-alias; lat/lon are also
// VALIDATED against the data (coordinate ranges) and range-sniffed as a fallback when no
// header matches (e.g. unnamed "X"/"col3" exports).
export function autoMatchColumns(
  parsed: ParsedCsv,
): Record<string, number | null> {
  const { headers, rows } = parsed;
  const normed = headers.map(norm);
  const mapping: Record<string, number | null> = {};
  const taken = new Set<number>();

  const claim = (key: string, idx: number) => {
    mapping[key] = idx;
    taken.add(idx);
  };

  // an ANALYTE match must carry data: the lb/ac export ships an empty "OM" column beside the real
  // "OM_LOI" — header priority alone would claim the empty one and silently drop the values.
  const hasDataCache = new Map<number, boolean>();
  const colHasData = (i: number): boolean => {
    let v = hasDataCache.get(i);
    if (v === undefined) {
      v = rows.slice(0, 50).some((r) => (r[i] ?? "").trim() !== "");
      hasDataCache.set(i, v);
    }
    return v;
  };

  for (const t of CSV_TARGETS) {
    mapping[t.key] = null;
    const isIdent = IDENT_KEYS.has(t.key);
    let idx = -1;
    if (t.patterns) {
      // PRIORITY tiers: the first pattern that matches ANY eligible header wins, so an export carrying
      // both Date_Sampled and Date_Reported (or P1 and P_Olsen) lands on the preferred column
      outer: for (const re of t.patterns) {
        for (let i = 0; i < normed.length; i++) {
          if (taken.has(i)) continue;
          if (!isIdent && (NON_VALUE_HEADER.test(normed[i]) || !colHasData(i)))
            continue;
          if (re.test(normed[i])) {
            idx = i;
            break outer;
          }
        }
      }
    } else {
      // legacy alias tiers (kept for targets without patterns): exact, then contains (≥3 chars)
      idx = normed.findIndex((h, i) => !taken.has(i) && t.aliases.includes(h));
      if (idx === -1)
        idx = normed.findIndex(
          (h, i) =>
            !taken.has(i) &&
            t.aliases.some((a) => a.length >= 3 && h.includes(a)),
        );
    }
    if (idx === -1) continue;
    // coordinates must actually look like coordinates — a MAJORITY gate (0.5), so a few
    // bad rows don't reject a legitimately-named column, but ppm-scale junk does fail
    if (
      t.key === "lat" &&
      numericShare(rows, idx, (n) => Math.abs(n) <= 90) < 0.5
    )
      continue;
    if (
      t.key === "lon" &&
      numericShare(rows, idx, (n) => Math.abs(n) <= 180) < 0.5
    )
      continue;
    claim(t.key, idx);
  }

  // Real GPS exports carry ≥3 decimal places (~100 m); lab values never do — so the sniff also
  // requires coordinate-grade precision, and never considers rec/rating/UOM columns.
  const coordGrade = (i: number) => {
    const sample = rows
      .slice(0, 25)
      .map((r) => (r[i] ?? "").trim())
      .filter(Boolean);
    if (!sample.length) return false;
    return (
      sample.filter((v) => /\.\d{3,}/.test(v)).length / sample.length >= 0.9
    );
  };
  const sniffable = (i: number) =>
    !taken.has(i) && !NON_VALUE_HEADER.test(normed[i]) && coordGrade(i);
  if (mapping.lat === null) {
    const idx = headers.findIndex(
      (_, i) =>
        sniffable(i) &&
        numericShare(rows, i, (n) => n !== 0 && Math.abs(n) <= 90) >= 0.9,
    );
    if (idx !== -1) claim("lat", idx);
  }
  if (mapping.lon === null) {
    // prefer a column that can ONLY be a longitude (|n| > 90)…
    let idx = headers.findIndex(
      (_, i) =>
        sniffable(i) &&
        numericShare(rows, i, (n) => Math.abs(n) > 90 && Math.abs(n) <= 180) >=
          0.9,
    );
    // …but |lon| < 90 across the eastern US — accept the remaining coordinate-like column
    if (idx === -1)
      idx = headers.findIndex(
        (_, i) =>
          sniffable(i) &&
          numericShare(rows, i, (n) => n !== 0 && Math.abs(n) <= 180) >= 0.9,
      );
    if (idx !== -1) claim("lon", idx);
  }
  return mapping;
}

// Only `ppm: true` analytes convert; a UOM saying ppm (or anything but lb) is 1. A header like
// K_lbA is a NAME, not a units declaration — conversion is the event's `units` (what the user said
// at upload), via the sample-lens / depth-normalize path.
export function uomFactors(
  parsed: ParsedCsv,
  mapping: Record<string, number | null>,
): Record<string, number> {
  const { headers, rows } = parsed;
  const out: Record<string, number> = {};
  for (const t of CSV_TARGETS) {
    if (!t.ppm) continue;
    const col = mapping[t.key];
    if (col == null || col + 1 >= headers.length) continue;
    if (!norm(headers[col + 1]).endsWith("uom")) continue;
    const v = rows.map((r) => (r[col + 1] ?? "").trim()).find(Boolean) ?? "";
    if (/lb/i.test(v)) out[t.key] = 0.5;
  }
  return out;
}

// Build the GeoJSON point set SS3 saves from the (user-confirmed) mapping. Rows with a
// missing/invalid coordinate are SKIPPED and counted — never silently dropped.
/*
 * ONE row's typed properties under a mapping — shared by `buildPointsFc` and the in-drop pairing
 * join (lib/samplePairing), so a joined results row can never convert differently from a GPS row.
 */
export function rowProperties(
  r: string[],
  mapping: Record<string, number | null>,
  factors: Record<string, number>,
): Record<string, unknown> {
  const properties: Record<string, unknown> = {};
  for (const t of CSV_TARGETS) {
    if (t.key === "lat" || t.key === "lon") continue;
    const col = mapping[t.key];
    if (col == null) continue;
    const raw = (r[col] ?? "").trim();
    if (!raw) continue;
    const n = Number(raw);
    // sampled_at goes through isoDateOf — raw lab dates ("9/ 2/2026") 500 in a server date bind
    properties[t.key] =
      t.key === "point_id"
        ? raw
        : t.key === "sampled_at"
          ? isoDateOf(raw)
          : Number.isFinite(n)
            ? n * (factors[t.key] ?? 1)
            : raw;
  }
  return properties;
}

export function buildPointsFc(
  parsed: ParsedCsv,
  mapping: Record<string, number | null>,
): { fc: unknown; saved: number; skipped: number } {
  const latCol = mapping.lat;
  const lonCol = mapping.lon;
  const factors = uomFactors(parsed, mapping);
  const features: unknown[] = [];
  let skipped = 0;
  for (const r of parsed.rows) {
    // Number('') is 0 — an empty coordinate must be INVALID, never the equator
    const rawLat = (latCol != null ? (r[latCol] ?? "") : "").trim();
    const rawLon = (lonCol != null ? (r[lonCol] ?? "") : "").trim();
    const lat = rawLat ? Number(rawLat) : NaN;
    const lon = rawLon ? Number(rawLon) : NaN;
    if (
      !Number.isFinite(lat) ||
      !Number.isFinite(lon) ||
      Math.abs(lat) > 90 ||
      Math.abs(lon) > 180
    ) {
      skipped++;
      continue;
    }
    const properties: Record<string, unknown> = rowProperties(
      r,
      mapping,
      factors,
    );
    // point_id is ALWAYS set (SS3b maps it to sample_id and the dashboard counts DISTINCT) —
    // synthesize a stable one when the CSV has no id column / an empty cell.
    if (!properties.point_id) properties.point_id = `P-${features.length + 1}`;
    features.push({
      type: "Feature",
      properties,
      geometry: { type: "Point", coordinates: [lon, lat] },
    });
  }
  return {
    fc: { type: "FeatureCollection", features },
    saved: features.length,
    skipped,
  };
}

// the matcher CONTRACT as data — Back-End-Async consumes this Compute's bulk lane ported this
// matcher verbatim; to kill the two-implementations drift risk the TABLE itself is exported as JSON
// (`csv-matcher-contract.
export function matcherContract(): unknown {
  return {
    version: 1,
    normalization: "lowercase; strip every character outside [a-z0-9]",
    non_value_header: NON_VALUE_HEADER.source,
    ident_keys: [...IDENT_KEYS],
    lb_ac_to_ppm_factor: 0.5,
    targets: CSV_TARGETS.map((t) => ({
      key: t.key,
      label: t.label,
      required: t.required ?? false,
      ppm: t.ppm ?? false,
      patterns: (t.patterns ?? []).map((re) => re.source),
    })),
  };
}
